import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { settingsAPI } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const Hilfe = () => (
  <details className="rounded-lg border bg-slate-50 p-3 text-sm text-slate-700" data-testid="pflicht-so-gehts">
    <summary className="cursor-pointer font-medium text-slate-800">So geht's</summary>
    <div className="mt-3 space-y-4">
      <section className="space-y-1">
        <p className="font-semibold text-slate-900">Zwei-Faktor-Anmeldung zurücksetzen</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>Klicken Sie in der Zeile der Person auf „Mehr ▾" → „2FA zurücksetzen".</li>
          <li>Bestätigen Sie die Sicherheitsabfrage.</li>
          <li>Die Person wird auf allen Geräten abgemeldet und kann die Zwei-Faktor-Anmeldung neu einrichten.</li>
        </ol>
        <p className="text-xs text-slate-500">Die eigene Zwei-Faktor-Anmeldung setzen Sie hier nicht zurück: Für ein neues Handy nutzen Sie Profil → Sicherheit → Authenticator-App. Wichtig: Das Einspielen einer alten Sicherung setzt auch den 2FA-Stand zurück.</p>
      </section>
      <section className="space-y-1">
        <p className="font-semibold text-slate-900">Konto sperren / entsperren</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>Klicken Sie in der Zeile der Person auf „Sperren" und geben Sie bei Bedarf einen Grund an.</li>
          <li>Die Person wird sofort auf allen Geräten abgemeldet und kann sich nicht mehr anmelden oder ein neues Passwort anfordern.</li>
          <li>Zum Aufheben klicken Sie auf „Entsperren" – die Anmeldung klappt dann wieder mit dem bisherigen Passwort, die 2FA bleibt eingerichtet.</li>
        </ol>
        <p className="text-xs text-slate-500">Sperren statt archivieren: Das Konto bleibt in der Liste, Daten und Verlauf bleiben erhalten. Wann, von wem und warum gesperrt wurde, sehen Sie mit einem Klick auf den Namen. Das eigene Konto und das letzte aktive Admin-Konto lassen sich nicht sperren; Admin-Konten sperrt nur ein Admin.</p>
      </section>
      <section className="space-y-1">
        <p className="font-semibold text-slate-900">Handy verloren?</p>
        <p>Konto sperren → 2FA zurücksetzen → entsperren. Danach richtet die Person die 2FA mit dem neuen Handy neu ein.</p>
      </section>
    </div>
  </details>
);

// Karte „Zwei-Faktor-Pflicht“ über der Liste der Verwaltungskonten.
export const PflichtKarte = ({ version, onZaehlungKlick }) => {
  const [stand, setStand] = useState(null);
  const [ladefehler, setLadefehler] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [nachfrage, setNachfrage] = useState(false);

  const laden = useCallback(async () => {
    try {
      const res = await settingsAPI.zweiFaktorPflicht();
      setStand(res.data);
      setLadefehler(false);
    } catch {
      setLadefehler(true);
    }
  }, []);

  useEffect(() => { laden(); }, [laden, version]);

  const setzen = async (aktiv) => {
    setSpeichert(true);
    try {
      const res = await settingsAPI.zweiFaktorPflichtSetzen(aktiv);
      setStand(res.data);
      toast.success(res.data.aktiv ? 'Zwei-Faktor-Pflicht ist an.' : 'Zwei-Faktor-Pflicht ist aus.');
    } catch {
      toast.error('Der Schalter konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
      setNachfrage(false);
    }
  };

  const aktiv = Boolean(stand && stand.aktiv);
  const ohne = stand ? stand.von - stand.mit_2fa : 0;
  return (
    <Card data-testid="pflicht-karte">
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h3 className="flex items-center gap-2 font-semibold text-slate-900 font-[Manrope]"><ShieldCheck className="h-5 w-5 text-green-600" />Zwei-Faktor-Pflicht</h3>
            <p className="text-sm text-slate-600">Vorher alle informieren. Wer noch keine 2FA hat, wird beim nächsten Login durch die Einrichtung geführt.</p>
            <p className="text-xs text-slate-500">Gilt nur für Verwaltungskonten. Mitarbeiter melden sich weiter nur mit Passwort an.</p>
            {stand && (
              <button type="button" onClick={onZaehlungKlick} className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline" data-testid="pflicht-zaehlung">
                {stand.mit_2fa} von {stand.von} Verwaltungskonten haben 2FA
              </button>
            )}
            {ladefehler && <p className="text-sm text-red-600" data-testid="pflicht-ladefehler">Stand konnte nicht geladen werden</p>}
          </div>
          <label className="flex shrink-0 items-center gap-2 text-sm font-medium text-slate-700">
            <Switch checked={aktiv} disabled={ladefehler || !stand || speichert} onCheckedChange={(an) => (an ? setNachfrage(true) : setzen(false))} data-testid="pflicht-schalter" />
            <span data-testid="pflicht-schalter-text">{aktiv ? 'An' : 'Aus'}</span>
          </label>
        </div>
        {stand && stand.notschalter && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="pflicht-notschalter">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Der Notschalter ZWEI_FAKTOR_PFLICHT_AUS ist gesetzt – die Pflicht wirkt gerade nicht.
          </div>
        )}
        <Hilfe />
      </CardContent>
      <Dialog open={nachfrage} onOpenChange={(o) => { if (!o && !speichert) setNachfrage(false); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pflicht-nachfrage">
          <DialogHeader>
            <DialogTitle>Zwei-Faktor-Pflicht einschalten?</DialogTitle>
            <DialogDescription>{ohne} von {stand ? stand.von : 0} Verwaltungskonten haben noch keine Zwei-Faktor-Anmeldung. Sie werden sofort abgemeldet und bei der nächsten Anmeldung durch die Einrichtung geführt. Wirklich einschalten?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNachfrage(false)} disabled={speichert} data-testid="pflicht-abbrechen">Abbrechen</Button>
            <Button variant="destructive" onClick={() => setzen(true)} disabled={speichert} data-testid="pflicht-einschalten">Einschalten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PflichtKarte;
